import {
  ID,
  LineItem,
  LineItemChanges,
  Lot,
  LotChanges,
  CustomColumnHeader,
  CustomColumnItem,
  LineItemsState,
  EventState,
} from 'types';
import { toLowerCaseAndTrim } from '@utils/strings';
import { isEmptyStateLot, isNewLot } from './emptyLot';
import { matchesNew } from './matchesNew';

/**
 * Determines if a value is a complex object (nested object or array) that should be excluded
 * from change detection
 * We only want to compare primitive values for changes
 */
const isComplexObject = (value: unknown): boolean => (
  value !== null && typeof value === 'object'
);

/**
 * Dynamically detects which fields in an object should be checked for changes
 * Returns an array of field names, excluding id and complex objects
 */
const getDynamicFields = (obj: Record<string, unknown>): string[] => {
  const fields: string[] = [];

  Object.keys(obj).forEach(key => {
    if (key !== 'id') {
      const value = obj[key];
      // Skip complex objects like arrays, nested objects, etc.
      if (!isComplexObject(value)) {
        fields.push(key);
      }
    }
  });

  return fields;
};

/**
 * Type guard to check if a value is a record-like object
 */
const isRecord = (value: unknown): value is Record<string, unknown> => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const FIELDS_TO_IGNORE = ['_collapsed'];

/**
 * Gets dynamic fields from both the original object and changes object
 * This ensures we check fields that only exist in changes (like _destroy)
 */
const getCombinedDynamicFields = (
  obj: Record<string, unknown>,
  changes: Record<string, unknown>,
): string[] => {
  const objFields = getDynamicFields(obj);
  const changeFields = getDynamicFields(changes);

  // Combine and deduplicate fields
  const allFields = [...new Set([...objFields, ...changeFields])];
  return allFields.filter((field: string): boolean => (
    !FIELDS_TO_IGNORE.includes(field)
  ));
};

type CustomColumnType = CustomColumnHeader | CustomColumnItem;
type ChangesType = CustomColumnType | LineItemChanges | LotChanges;
type ObjType = CustomColumnType | LineItem | Lot;

export interface HasChangedFieldsParams {
  changes: ChangesType;
  obj?: ObjType;
}

/**
 * Checks if a value is considered empty (null, undefined, empty string, or empty object)
 * Used to avoid detecting changes when both old and new values are effectively empty
 * Note: Arrays are excluded from field detection by getDynamicFields, so no array check needed
 */
const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (typeof value === 'object' && value !== null && Object.keys(value).length === 0) return true;
  return false;
};

/**
 * Compares specific fields between an original object and its changes to detect if any fields
 * have actually changed
 * Uses runtime type detection to apply appropriate comparison logic (number, boolean, or string)
 * This is the core function that determines if there are meaningful changes in an object
 */
// This is a shallow comparison, but it is enough for our use case; and
// the obj has all the fields and the changes has only the fields that have changed.
export const hasChangedFields = ({ changes, obj }: HasChangedFieldsParams): boolean => {
  if (!obj) return false;
  if (!changes) return true;

  // Use dynamic field detection if fields are not provided
  // Get fields from both objects to catch fields that only exist in changes (like _destroy)
  const fieldsToCheck = isRecord(obj) && isRecord(changes)
    ? getCombinedDynamicFields(obj, changes)
    : [];

  return fieldsToCheck.some((field): boolean => {
    const changedValue = changes[field as keyof typeof changes];

    if (changedValue === undefined) return false;
    const value = obj[field as keyof typeof obj];

    // If both values are effectively empty, no change
    if (isEmptyValue(changedValue) && isEmptyValue(value)) {
      return false;
    }

    // Use the original value's type to determine comparison method
    if (typeof value === 'number' || (value === null && typeof changedValue === 'number')) {
      const numberValue: number = value === null || value === undefined ? 0 : Number(value);
      return numberValue !== Number(changedValue);
    }

    if (typeof value === 'boolean' || (value === null && typeof changedValue === 'boolean')) {
      const booleanValue: boolean = value === null || value === undefined ? false : Boolean(value);
      return booleanValue !== Boolean(changedValue);
    }

    // Default to string comparison
    const stringValue = value === null || value === undefined ? '' : String(value);
    return stringValue.trim() !== String(changedValue)?.trim();
  });
};

/**
 * Utility function to compare two IDs for equality, handling various edge cases
 * Treats null/undefined IDs as 0, and handles "new" record IDs specially
 */
export const checkSameId = (idA?: ID | null, idB?: ID | null): boolean => {
  let a = idA;
  let b = idB;

  if (!a) a = 0; // Default to 0 if idA is null or undefined
  if (!b) b = 0; // Default to 0 if idB is null or undefined
  if (matchesNew(a) || matchesNew(b)) return a === b;

  const numA = Number(a);
  const numB = Number(b);
  if (Number.isNaN(numA) || Number.isNaN(numB)) return String(a) === String(b);
  return numA === numB;
};

export interface HasChangesParams {
  changes: ChangesType;
  id: ID;
  list?: ObjType[]
}

/**
 * Determines if a record has changes by checking various conditions:
 * - New records have changes unless marked for destruction
 * - Existing records marked for destruction have changes
 * - Existing records have changes if their fields have been modified
 */
export const hasChanges = ({
  changes,
  id,
  list
}: HasChangesParams): boolean => {
  // If there is a new record, we have changes.
  if (matchesNew(id)) {
    // A new record marked for destruction should be ignored.
    return !changes?._destroy;
  }
  // If there are no changes, it should be ignored.
  if (!changes) return false;
  // A persisted record marked for destruction, we have changes.
  if (changes._destroy) return true;
  const persisted = list?.find((obj: ObjType) => checkSameId(obj.id, id));

  // If there are changes in the record fields, we have changes.
  return hasChangedFields({ changes, obj: persisted });
};

/**
 * Checks if any custom column headers in a lot have changes
 * Iterates through all custom column header changes and validates each one
 */
export const customColumnHeadersHasChanges = ({ lot, lotChanges }: {
  lot?: Lot;
  lotChanges?: LotChanges;
}): boolean => {
  if (!lot || !lotChanges) return false;
  const list: CustomColumnHeader[] = lot.custom_column_headers;
  const listChanges: Record<ID, CustomColumnHeader> = lotChanges.custom_column_headers || {};
  const hasListChanges = Boolean(listChanges && Object.keys(listChanges)?.length);

  if (!hasListChanges) return false;
  // Find the first custom column header that has changes.
  return Object.keys(listChanges).some((id: ID): boolean => {
    const changes: CustomColumnHeader = listChanges[id];

    // Skip undefined changes
    if (!changes) return false;

    // If there are changes in the custom column header fields, we have changes.
    return hasChanges({ changes, id, list });
  });
};

/**
 * Checks if any custom column items in a line item have changes
 * Handles special cases like deleted headers and validates each custom column item change
 */
export const customColumnItemsHasChanges = ({ lineItem, lineItemChanges, lotChanges }: {
  lineItem?: LineItem;
  lineItemChanges?: LineItemChanges;
  lotChanges: LotChanges;
}): boolean => {
  if (!lineItem || !lineItemChanges) return false;
  const list: CustomColumnItem[] = lineItem.custom_column_items || [];
  const listChanges: Record<ID, CustomColumnItem> = lineItemChanges.custom_column_items || {};
  const hasListChanges = Boolean(listChanges && Object.keys(listChanges)?.length);

  if (!hasListChanges) return false;
  const headers = lotChanges?.custom_column_headers || {};
  const deletedHeaders = Object.values(headers).filter((header): boolean => (
    Boolean(header._destroy)
  ));

  // Find the first custom column item that has changes.
  return Object.keys(listChanges).some((id: ID): boolean => {
    const isHeaderDeleted = deletedHeaders.some((header): boolean => (
      checkSameId(header.id, listChanges[id].custom_column_header_id)
    ));

    if (isHeaderDeleted) return false;
    const changes: CustomColumnItem = listChanges[id];
    // If there is a new record, we have changes.
    if (matchesNew(changes.id)) {
      const isDestroyed = changes._destroy || !changes.display_text?.trim()?.length;

      // A new record marked for destruction should be ignored.
      return !isDestroyed;
    }
    // Use dynamic field detection for custom column items
    return hasChanges({ changes, id, list });
  });
};

/**
 * Checks if any line items in a lot have changes
 * Validates both the line item fields and their associated custom column items
 */
export const lineItemsHasChanges = ({ lot, lotChanges }: {
  lot?: Lot;
  lotChanges?: LotChanges;
}): boolean => {
  if (!lot || !lotChanges) return false;
  const list: LineItem[] = lot.line_items;
  const listChanges: Record<ID, LineItemChanges> = lotChanges.line_items || {};
  const hasListChanges = Boolean(listChanges && Object.keys(listChanges)?.length);

  if (!hasListChanges) return false;
  // Find the first line item that has changes.
  return Object.keys(listChanges).some((id: ID): boolean => {
    const changes: LineItemChanges = listChanges[id];

    // Skip undefined changes
    if (!changes) return false;

    // If there are changes in the line item fields, we have changes.
    if (hasChanges({ changes, id, list })) return true;
    const lineItem = list?.find((obj: LineItem) => checkSameId(obj.id, id));

    // Check for changes on the Custom Column Items of the line item.
    return customColumnItemsHasChanges({ lineItem, lineItemChanges: changes, lotChanges });
  });
};

/**
 * Checks if the event-level properties (currency, incumbent) have changed
 * Compares current event state with original state
 */
export const hasEventChanges = ({ event }: {
  event?: EventState;
}): boolean => {
  if (!event) return false;
  const { currency, originalCurrency, eventIncumbentId, originalEventIncumbentId } = event;

  // If the event currency has a change, we have changes.
  if (toLowerCaseAndTrim(currency) !== toLowerCaseAndTrim(originalCurrency)) return true;

  // If the event incumbent id has a change, we have changes.
  if (!checkSameId(eventIncumbentId, originalEventIncumbentId)) return true;

  return false;
};

/**
 * Check for the lot empty state, when we don't have any lots,
 * we are creating a default empty new lot, and that's not a change.
 * This prevents detecting changes when the system automatically creates an empty default lot
 */
export const hasOnlyDefaultLot = ({ lots, lotsChanges }: {
  lots?: Lot[];
  lotsChanges?: Record<ID, LotChanges>;
}): boolean => {
  // If there are any existing lots, we're not creating the default lot.
  if (lots?.length) return false;
  const numberOfLotsChanged = (lotsChanges && Object.keys(lotsChanges)?.length) || 0;

  // If we have more than one lot changed, we're not creating the default lot.
  if (numberOfLotsChanged > 1) return false;
  const firstChange = lotsChanges && Object.values(lotsChanges)[0];

  // If we don't have any changes, we're not creating the default lot.
  if (!firstChange) return false;

  // If the lot is not new and still empty, we're creating the default lot.
  return isNewLot(firstChange) && isEmptyStateLot(firstChange);
};

/**
 * Checks if any lots have meaningful changes by validating lot fields and their associated data
 * Examines lot-level fields, line items, and custom column headers for each lot
 */
export const checkForLotsChanges = ({ lots, lotsChanges }: {
  lots?: Lot[];
  lotsChanges: Record<ID, LotChanges>;
}): boolean => (
  Object.keys(lotsChanges).some((id: ID): boolean => {
    const lotChanges = lotsChanges[id];

    // If there are changes on the lot fields (name, description, incumbent_id), we have changes.
    if (hasChanges({ changes: lotChanges, id, list: lots })) return true;
    const lot = lots?.find((obj: Lot) => checkSameId(obj.id, id));

    // Check for changes on the Line Items and Custom Column Headers of the lot.
    return lineItemsHasChanges({ lot, lotChanges })
      || customColumnHeadersHasChanges({ lot, lotChanges });
  })
);

/**
 * Main function that determines if there are any changes in the entire line items state
 * Checks event changes, validates lot changes, and handles the special case of default empty lots
 */
export const checkForChanges = (state: Partial<LineItemsState>): boolean => {
  const { originalLots: lots, changes: lotsChanges, event } = state;

  // If the event has a change, we have changes.
  if (hasEventChanges({ event })) return true;

  // Check if the lot changes object or its children are undefined
  if (lotsChanges === undefined || !Object.keys(lotsChanges)?.length) return false;

  // If there is only one lot changed, we need to check if it is empty.
  if (hasOnlyDefaultLot({ lots, lotsChanges })) return false;

  // There are more than one lot with changes, we need to evaluate the others
  return checkForLotsChanges({ lots, lotsChanges });
};

/**
 * Checks if a specific custom column header has changed
 * Compares the current header state with the original persisted state
 */
export const isCustomColumnHeaderChanged = ({
  headerId,
  lotId,
  state,
}: {
  headerId: ID;
  lotId: ID;
  state: LineItemsState;
}): boolean => {
  if (matchesNew(headerId)) return true;

  const changes = state.changes[lotId]?.custom_column_headers?.[headerId];
  const persisted = state.originalLots.find(lot => (
    checkSameId(lot.id, lotId)
  ))?.custom_column_headers?.find(header => (
    checkSameId(header.id, headerId)
  ));
  if (!changes || !persisted) return false;

  return hasChangedFields({ changes, obj: persisted });
};

/**
 * Checks if a specific custom column item has changed
 * Navigates through the state hierarchy to find the original item and compare with changes
 */
export const isCustomColumnItemChanged = ({
  customColumnItemId,
  lineItemId,
  lotId,
  state,
}: {
  customColumnItemId: ID;
  lineItemId: ID;
  lotId: ID;
  state: LineItemsState;
}): boolean => {
  if (matchesNew(customColumnItemId)) return true;

  const changes =
    state.changes[lotId]?.line_items?.[lineItemId]?.custom_column_items?.[customColumnItemId];
  if (!changes) return false;

  const persisted = state.originalLots.find(lot => (
    checkSameId(lot.id, lotId)
  ))?.line_items?.find(lineItem => (
    checkSameId(lineItem.id, lineItemId)
  ))?.custom_column_items?.find(item => (
    checkSameId(item.id, customColumnItemId)
  ));
  if (!persisted) return false;

  return hasChangedFields({ changes, obj: persisted });
};

/**
 * Checks if a specific line item has changed
 * Validates both standard line item fields and associated custom column items
 * Optimized to only check custom column items that actually have changes
 */
export const isLineItemChanged = ({
  lineItemId,
  lotId,
  state,
}: {
  lineItemId: ID;
  lotId: ID;
  state: LineItemsState;
}): boolean => {
  if (matchesNew(lineItemId)) return true;

  const changes = state.changes[lotId]?.line_items?.[lineItemId];
  if (!changes) return false;

  const persisted = state.originalLots.find(lot => (
    checkSameId(lot.id, lotId)
  ))?.line_items?.find(lineItem => (
    checkSameId(lineItem.id, lineItemId)
  ));
  if (!persisted) return false;

  // Check if standard line item fields changed
  if (hasChangedFields({ changes, obj: persisted })) {
    return true;
  }

  // Check if any custom column items changed - but only check those that actually have changes
  const customColumnItemChanges = changes.custom_column_items || {};
  if (Object.keys(customColumnItemChanges).length === 0) return false;

  // Only check the specific custom column items that have changes
  return Object.keys(customColumnItemChanges).some(customColumnItemId => {
    const customColumnItem = persisted.custom_column_items?.find(item =>
      checkSameId(item.id, customColumnItemId));
    if (!customColumnItem) return matchesNew(customColumnItemId);

    return hasChangedFields({
      changes: customColumnItemChanges[customColumnItemId],
      obj: customColumnItem
    });
  });
};

/**
 * Checks if a specific lot has changed
 * Validates lot-level fields and recursively checks for changes in line items and
 * custom column headers
 */
export const isLotChanged = ({
  lotId,
  state,
}: {
  lotId: ID;
  state: LineItemsState;
}): boolean => {
  if (matchesNew(lotId)) return true;
  const changes = state.changes[lotId];
  const persisted = state.originalLots.find(lot => (
    checkSameId(lot.id, lotId)
  ));
  if (!changes || !persisted) return false;

  if (hasChangedFields({ changes, obj: persisted })) {
    return true;
  }

  // Check for changes on the Line Items and Custom Column Headers of the lot.
  return lineItemsHasChanges({ lot: persisted, lotChanges: changes })
    || customColumnHeadersHasChanges({ lot: persisted, lotChanges: changes });
};

/**
 * Optimized function to check if any changes exist in the line items state
 * @param state - The current LineItemsState
 * @param isLineItemChangedFn - Function to check if a specific line item changed
 * @returns boolean - True if changes exist, false otherwise
 */
export const hasLineItemStateChanges = (
  state: LineItemsState,
  isLineItemChangedFn: (params: {
    lineItemId: string;
    lotId: string;
    state: LineItemsState;
  }) => boolean,
): boolean => {
  // Quick check: if no changes object exists, no changes
  if (!state.changes || Object.keys(state.changes).length === 0) {
    return false;
  }

  // For small changes, use optimized checking
  const lotIds = Object.keys(state.changes);
  if (lotIds.length === 1) {
    const lotId = lotIds[0];
    const lotChanges = state.changes[lotId];

    // If only line items changed in one lot, check them efficiently
    if (lotChanges?.line_items &&
       !lotChanges.name &&
       !lotChanges.description &&
       !lotChanges.incumbent_id) {
      const lineItemIds = Object.keys(lotChanges.line_items);

      // Check line items individually (handles both single and multiple items)
      return lineItemIds.some(lineItemId =>
        isLineItemChangedFn({ lineItemId, lotId, state }));
    }
  }

  // Fall back to full table check for complex changes
  return checkForChanges(state);
};
